/**
 * <s-promo-popup> — opens its inner <dialog> after a delay, once per visitor.
 *
 * Attributes:
 *   data-delay      — seconds before opening
 *   data-hide-days  — days to stay hidden after the visitor closes it
 *   data-block-id   — scopes the storage keys to this block
 *
 * A visitor who submits the pop-up's form is marked as subscribed and never sees it again.
 * On success it closes and opens the separate thank-you dialog named by data-thanks-id.
 * If a submit falls back to a full reload, the result is shown after the page loads.
 * In the theme editor it never auto-opens; selecting the block previews it.
 */
class SPromoPopup extends HTMLElement {
  #dialog;
  #ac;
  #timer;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const key = `promo-popup:${this.dataset.blockId || 'default'}`;

    this.#dialog.addEventListener('close', () => {
      this.#store(`${key}:dismissed`, String(Date.now()));
    }, opts);

    this.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      event.preventDefault();
      this.#submit(form, key);
    }, opts);

    // Returning from this pop-up's own form submission: show the result, then stop showing it.
    if (this.#read(`${key}:submitted`, sessionStorage)) {
      this.#remove(`${key}:submitted`, sessionStorage);
      const succeeded = !this.querySelector('[role="alert"]');
      if (succeeded) {
        this.#store(`${key}:subscribed`, '1');
        this.#showThanks();
      } else {
        this.#open();
      }
      return;
    }

    if (window.Shopify?.designMode) {
      const thanks = () => document.getElementById(this.dataset.thanksId || '');
      document.addEventListener('shopify:block:select', (event) => {
        if (!(event.target instanceof Node)) return;
        if (this.contains(event.target)) this.#open();
        else if (thanks()?.contains(event.target)) this.#showThanks();
      }, opts);
      document.addEventListener('shopify:block:deselect', (event) => {
        if (!(event.target instanceof Node)) return;
        if (this.contains(event.target)) this.#dialog.close();
        else if (thanks()?.contains(event.target)) thanks().close();
      }, opts);
      return;
    }

    if (this.#read(`${key}:subscribed`)) return;

    const dismissedAt = Number(this.#read(`${key}:dismissed`) || 0);
    const hideMs = Number(this.dataset.hideDays || 7) * 86400000;
    if (dismissedAt && Date.now() - dismissedAt < hideMs) return;

    const delayMs = Math.max(0, Number(this.dataset.delay || 0)) * 1000;
    this.#timer = window.setTimeout(() => this.#open(), delayMs);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearTimeout(this.#timer);
  }

  // Show the thank-you immediately (the browser already validated the email), then post in
  // the background. If the post needs a spam check or fails, fall back to a normal submit.
  async #submit(form, key) {
    const body = new FormData(form);
    this.#dialog.close();
    this.#showThanks();

    try {
      const response = await fetch(form.action, { method: 'POST', body, keepalive: true });
      const url = new URL(response.url);
      const postedTo = new URL(form.action, window.location.href).pathname;
      // A spam-check challenge, or staying on the form endpoint (validation errors), needs the native flow.
      const needsNativeSubmit = !response.ok
        || url.pathname.includes('/challenge')
        || (url.pathname === postedTo && !url.search.includes('posted=true'));
      if (!needsNativeSubmit) {
        this.#store(`${key}:subscribed`, '1');
        form.reset();
        return;
      }
    } catch {
      // Network failure: fall through to a normal submit.
    }

    // Leave the thank-you open; the native submit navigates away and the result shows after it.
    this.#store(`${key}:submitted`, '1', sessionStorage);
    HTMLFormElement.prototype.submit.call(form);
  }

  // The thank-you pop-up lives outside this element, so the signup pop-up can close first.
  #showThanks() {
    const thanks = document.getElementById(this.dataset.thanksId || '');
    if (thanks instanceof HTMLDialogElement && !thanks.open) thanks.showModal();
  }

  #open() {
    // Never stack on top of another open modal such as the cart drawer or search.
    if (this.#dialog.open || document.querySelector('dialog:modal')) return;
    this.#dialog.showModal();
  }

  #read(name, storage = localStorage) {
    try { return storage.getItem(name); } catch { return null; }
  }

  #store(name, value, storage = localStorage) {
    try { storage.setItem(name, value); } catch { /* storage unavailable */ }
  }

  #remove(name, storage = localStorage) {
    try { storage.removeItem(name); } catch { /* storage unavailable */ }
  }
}

customElements.define('s-promo-popup', SPromoPopup);
